// ROIS Theme Definition
// Tema completo autocontenido para el cliente ROIS

import { Theme, Colors } from '../types';

// ========================================
// COLORES ROIS (DEFINICIÓN ESPECÍFICA PARA ROIS)
// Colores actualizados según diseño Figma y pantallas de la aplicación
// ========================================

const roisLightColors: Colors = {
  // Colores principales
  white: '#FFFFFF',
  black: '#111111',

  // Grises (mantenidos iguales - son neutros)
  greyVeryLight: '#F4F1F2',
  greyVerySoft: '#F6F6F6',
  greyLight: '#D9D9D9',
  greySilver: '#959595',
  lightGreyWhite: '#CCCCCC',
  greyBackground: '#19191D66',
  greyBoard: '#042F3D', // CAMBIO: Finalizado (Figma)
  greyVeryDark: '#575757',
  greyDark: '#757575',
  greyClear: '#F5F5F5',
  greySoftLight: '#A0A0A0',
  paleGrey: '#EAEAEA',
  paleDarkGrey: '#AEAEAE',
  darkGrey: '#3D3D3D',
  mediumGrey: '#9F9F9F',
  darkGrayishGrey: '#747474',
  pureGrey: '#808080',

  // Rojos (actualizados según Figma)
  softRed: '#FEE8E7',
  softDarkWhite: '#F0EAEC',
  red: '#EE6055', // CAMBIO: Anulado (Figma)
  lightRed: '#FFF7F6',
  verySoftRed: '#FAF1F0',
  veryClearRed: '#F8EBEB',
  clearRed: '#FFD9D7',
  lightRose: '#FBE2E0',
  lightCoralRed: '#F6655A',
  darkBrickRed: '#A62F27',
  deepBrickRed: '#691E19',
  garnet: '#686890', // CAMBIO: Color principal ROIS (Botón Default - Figma)
  garnetLight: '#68689033',
  garnetClear: '#6868901A',
  darkBurgundyRed: '#4E4E6C', // CAMBIO: Color hover ROIS (Botón Hover - Figma)
  veryDarkBurgundyRed: '#4E4E6C', // Mismo que hover para consistencia
  deepRedWine: '#440F1D',
  DustyRose: '#D88C87',
  lightDustyRose: '#AE7F8C',
  darkRoseRed: '#8E495C',
  paleRose: '#F1EAEC',
  lightPink: '#DC8E88',
  lightPastelPink: '#F1D8D6',
  veryClearPink: '#F1DDE3',

  // Verdes (actualizados según Figma)
  softGreen: '#ECF7ED',
  green: '#60D394', // CAMBIO: Validado (Figma)
  veryClearGreen: '#EEF5EF',
  mossGreen: '#5B8B44',
  forestGreen: '#37833B',
  darkForestGreen: '#426531',
  lightPearGreen: '#B1D3A0',
  veryLightPastelGreen: '#EAFDED',
  sageGreen: '#8DB38E',

  // Otros colores (actualizados según Figma)
  gold: '#FFD97D', // CAMBIO: Parcial (Figma)
  goldSoft: '#F8C28A',
  // Ámbar de `gold` para LETRA sobre fondo claro: `gold` es un color de fondo
  // (1,36:1 como texto). Este da ≥4,5:1 sobre blanco y sobre las filas grises.
  goldText: '#8A5A00', // 5,9:1 sobre blanco
  // Etiquetas: letra tonal (≥4,5:1 sobre su fondo) y fondos con letra blanca legible.
  tagTextGold: '#5C4100',
  tagTextBlue: '#003C54',
  tagTextBlueSoft: '#113045',
  tagTextGreen: '#0E3720',
  tagTextOrange: '#391E01',
  tagTextPink: '#80009E',
  tagSurfaceRed: '#C84941',
  tagSurfaceTeal: '#008276',
  pink: '#F9DFFF',
  purple: '#A92DC8',
  blue: '#70D6FF', // CAMBIO: Abierta (Figma)
  blueSoft: '#9AC0DA',
  blueRouteInProgress: '#2E7BA6', // RV en progreso (contraste 4,66:1 con blanco)
  teal: '#009688', // Pendent de certificar (post-validación, pendiente cobro Generalitat)

  // Colores específicos ROIS (nuevos)
  blueOT: '#076D8C', // Azul O.T principal
  blueOTSecondary: '#076D8C', // Azul O.T secundario
  blueActuation: '#0A91BA', // Azul Actuación principal
  blueActuationSecondary: '#0A91BA', // Azul Actuación secundario
  blueCommunication: '#B4E8F8', // Azul Comunicación claro
  blueCommunicationSecondary: '#B4E8F8', // Azul Comunicación secundario
  backgroundLila: '#AEADF04F', // Fondo lila con opacidad 31% (Figma: rgba(174, 173, 240, 0.31))
  navigationActive: '#56567666', // Fondo navegación activa (Figma)

  // Colores adicionales para componentes
  greenTransparent: 'rgba(0, 128, 21, 0.1)', // Verde con 10% opacidad (Badge internal)
  orangeTransparent: 'rgba(239, 125, 5, 0.1)', // Naranja con 10% opacidad (Badge external)
  secondaryOrange: '#EF7D05', // Naranja secundario (basado en Figma)
  background: '#FFFFFF', // Fondo principal (igual que white)
  text: '#111111', // Texto principal (igual que black)
  border: '#D9D9D9', // Borde (igual que greyLight)
  primary: '#686890', // Color primario (igual que garnet - botón default)
  error: '#EE6055', // Color error (igual que red - anulado)

  // Estados de segmentos de rutas de vigilancia (Timer iteration)
  routeStatus: {
    completed: '#60D394', // = green
    paused: '#EF7D05', // = secondaryOrange
    active: '#70D6FF', // = blue
  },
};

const roisDarkColors: Colors = {
  // Colores principales (invertidos para tema oscuro - exactamente igual a CORA)
  white: '#1a1a1a',
  black: '#ffffff',

  // Grises (invertidos para tema oscuro - exactamente igual a CORA)
  greyVeryLight: '#2a2a2a',
  greyVerySoft: '#333333',
  greyLight: '#404040',
  greySilver: '#6a6a6a',
  lightGreyWhite: '#333333',
  greyBackground: '#19191D66',
  greyBoard: '#5a5a5a',
  greyVeryDark: '#f0f0f0',
  greyDark: '#8a8a8a',
  greyClear: '#2d2d2d',
  greySoftLight: '#5f5f5f',
  paleGrey: '#151515',
  paleDarkGrey: '#515151',
  darkGrey: '#c2c2c2',
  mediumGrey: '#606060',
  darkGrayishGrey: '#8b8b8b',
  pureGrey: '#7f7f7f',

  // Rojos (actualizados para tema oscuro ROIS)
  softRed: '#FEE8E7',
  softDarkWhite: '#F0EAEC',
  red: '#EE6055', // CAMBIO: Anulado (Figma)
  lightRed: '#FFF7F6',
  verySoftRed: '#FAF1F0',
  veryClearRed: '#F8EBEB',
  clearRed: '#FFD9D7',
  lightRose: '#FBE2E0',
  lightCoralRed: '#F6655A',
  darkBrickRed: '#A62F27',
  deepBrickRed: '#691E19',
  garnet: '#686890', // CAMBIO: Color principal ROIS (Botón Default - Figma)
  garnetLight: '#68689033',
  garnetClear: '#6868901A',
  darkBurgundyRed: '#4E4E6C', // CAMBIO: Color hover ROIS (Botón Hover - Figma)
  veryDarkBurgundyRed: '#4E4E6C', // Mismo que hover para consistencia
  deepRedWine: '#440F1D',
  DustyRose: '#D88C87',
  lightDustyRose: '#AE7F8C',
  darkRoseRed: '#8E495C',
  paleRose: '#F1EAEC',
  lightPink: '#DC8E88',
  lightPastelPink: '#F1D8D6',
  veryClearPink: '#F1DDE3',

  // Verdes (actualizados para tema oscuro ROIS)
  softGreen: '#ECF7ED',
  green: '#60D394', // CAMBIO: Validado (Figma)
  veryClearGreen: '#EEF5EF',
  mossGreen: '#5B8B44',
  forestGreen: '#37833B',
  darkForestGreen: '#426531',
  lightPearGreen: '#B1D3A0',
  veryLightPastelGreen: '#EAFDED',
  sageGreen: '#8DB38E',

  // Otros colores (actualizados para tema oscuro ROIS)
  gold: '#FFD97D', // CAMBIO: Parcial (Figma)
  goldSoft: '#F8C28A',
  goldText: '#FFD97D', // oscuro: el propio gold, 12,8:1 sobre #1a1a1a
  // Etiquetas: letra tonal (≥4,5:1 sobre su fondo) y fondos con letra blanca legible. En oscuro la letra «white» es #1a1a1a: rojo y verde azulado se quedan como están.
  tagTextGold: '#5C4100',
  tagTextBlue: '#003C54',
  tagTextBlueSoft: '#113045',
  tagTextGreen: '#0E3720',
  tagTextOrange: '#391E01',
  tagTextPink: '#80009E',
  tagSurfaceRed: '#EE6055',
  tagSurfaceTeal: '#009688',
  pink: '#F9DFFF',
  purple: '#A92DC8',
  blue: '#70D6FF', // CAMBIO: Abierta (Figma)
  blueSoft: '#9AC0DA',
  blueRouteInProgress: '#2E7BA6', // RV en progreso (contraste 4,66:1 con blanco)
  teal: '#009688', // Pendent de certificar (post-validación, pendiente cobro Generalitat)

  // Colores específicos ROIS (nuevos - tema oscuro)
  blueOT: '#076D8C', // Azul O.T principal
  blueOTSecondary: '#076D8C', // Azul O.T secundario
  blueActuation: '#0A91BA', // Azul Actuación principal
  blueActuationSecondary: '#0A91BA', // Azul Actuación secundario
  blueCommunication: '#B4E8F8', // Azul Comunicación claro
  blueCommunicationSecondary: '#B4E8F8', // Azul Comunicación secundario
  backgroundLila: '#AEADF04F', // Fondo lila con opacidad 31% (Figma: rgba(174, 173, 240, 0.31))
  navigationActive: '#56567666', // Fondo navegación activa (Figma)

  // Colores adicionales para componentes (tema oscuro)
  greenTransparent: 'rgba(0, 128, 21, 0.1)', // Verde con 10% opacidad (Badge internal)
  orangeTransparent: 'rgba(239, 125, 5, 0.1)', // Naranja con 10% opacidad (Badge external)
  secondaryOrange: '#EF7D05', // Naranja secundario
  background: '#1a1a1a', // Fondo principal oscuro (igual que white dark)
  text: '#ffffff', // Texto principal claro (igual que black dark)
  border: '#404040', // Borde oscuro (igual que greyLight dark)
  primary: '#686890', // Color primario (mantenido igual)
  error: '#EE6055', // Color error (mantenido igual)

  // Estados de segmentos de rutas de vigilancia (Timer iteration)
  routeStatus: {
    completed: '#60D394', // = green
    paused: '#EF7D05', // = secondaryOrange
    active: '#70D6FF', // = blue
  },
};

// ========================================
// TEMA COMPLETO ROIS (PERSONALIZADO PARA ROIS)
// ========================================

const roisBaseTheme = {
  // Espaciado ROIS (valores reales del proyecto - exactamente igual a CORA)
  spaces: {
    xxxs: 0.5,
    xxs: 0.8,
    xs: 1.4,
    s: 1.6,
    m: 2.5,
    l: 3.2,
    xl: 4,
    xxl: 6.4,
    xxxl: 8,
  },

  // Breakpoints ROIS (valores reales del proyecto - exactamente igual a CORA)
  breakPoints: {
    portraitTablet: 60, // 60 * 0.625 = 37.5rem = 600px (Tablet vertical)
    landscapeTablet: 102.3,
    smallDesktop: 186,
  },

  percentBaseResponsive: 0.625,

  // Tipografía ROIS (valores reales del proyecto - exactamente igual a CORA)
  fontSize: {
    XXXS: '1rem',
    XXS: '1.2rem',
    XS: '1.4rem',
    S: '1.6rem',
    M: '1.8rem',
    L: '2rem',
    XL: '3.4rem',
  },

  fontWeight: {
    regular: 400,
    medium: 500,
    semiBold: 600,
  },

  // Bordes ROIS (exactamente igual a CORA)
  borderRadius: {
    none: '0',
    sm: '0.25rem',
    md: '0.5rem',
    lg: '0.75rem',
    xl: '1rem',
    full: '9999px',
  },

  // Sombras ROIS (exactamente igual a CORA)
  shadows: {
    none: 'none',
    sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
    md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
    xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    header: '0 0.2rem 0.4rem rgba(0, 0, 0, 0.24)',
    modal: '0.03rem 0.5rem 1.3rem rgba(0, 0, 0, 0.15)',
    toast: '0 0.8rem 0.8rem rgba(0, 0, 0, 0.25)',
    dropdown: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)',
    card: '0 2px 4px rgba(0, 0, 0, 0.1)',
    camera: '0 0 13px 3px rgba(0, 0, 0, 0.5)',
    intense: '-5px 8px 19px -7px rgba(0, 0, 0, 0.74)',
  },

  // Transiciones ROIS (exactamente igual a CORA)
  transitions: {
    fast: '150ms ease-in-out',
    normal: '300ms ease-in-out',
    slow: '500ms ease-in-out',
  },

  // Z-Index ROIS (exactamente igual a CORA)
  zIndex: {
    sticky: 99,
    dropdown: 1020,
    fixed: 1030,
    modal: 1040,
    popover: 1050,
    tooltip: 1060,
  },

  // Tamaños ROIS (agrandados para mejor visibilidad)
  sizes: {
    logoWidth: '11rem' /* CAMBIO: Agrandado de 9rem a 11rem */,
    logoHeight: '3.2rem' /* CAMBIO: Agrandado de 2.6rem a 3.2rem */,
    headerMaxHeight: '7.7rem',
    iconSize: '2.4rem' /* CAMBIO: Agrandado de 2rem a 2.4rem */,
    buttonHeight: '4rem',
    modalMinWidth: '33.8rem',
  },

  // Opacidad ROIS (exactamente igual a CORA)
  opacity: {
    disabled: 0.6,
    overlay: 0.24,
    modalOverlay: 0.3,
    hover: 0.8,
    focus: 0.15,
  },

  // Helpers calculados ROIS (valores reales del proyecto - exactamente igual a CORA)
  spacing: {
    xxxs: '0.5rem',
    xxs: '0.8rem',
    xs: '1.4rem',
    s: '1.6rem',
    m: '2.5rem',
    l: '3.2rem',
    xl: '4rem',
    xxl: '6.4rem',
    xxxl: '8rem',
  },
};

// ========================================
// TEMAS ROIS EXPORTADOS
// ========================================

export const roisLightTheme: Theme = {
  ...roisBaseTheme,
  colors: roisLightColors,
};

export const roisDarkTheme: Theme = {
  ...roisBaseTheme,
  colors: roisDarkColors,
};

// Export de todos los temas ROIS
export const roisThemes = {
  light: roisLightTheme,
  dark: roisDarkTheme,
} as const;
